import { Component, inject, output, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { CardComponent } from '@vectronic/ui';
import {
  DatatableSelectionFilter,
  EditDatatableSelectionFilterPayload,
  SaveDatatableSelectionFilterPayload,
} from '../../types/datatable-configuration';
import { DatatableFilter } from '../../types/datatable-filter';
import { DatatableFiltersComponent } from '../datatable-filters/datatable-filters.component';

@Component({
  selector: 'table-datatable-filter-dialog',
  templateUrl: './datatable-filter-dialog.component.html',
  styleUrls: ['./datatable-filter-dialog.component.scss'],
  imports: [CardComponent, MatDialogModule, DatatableFiltersComponent],
})
export class DatatableFilterDialogComponent {
  filterChange = output<{ title: string; value: any }>();
  selectionFilterChange = output<number | undefined>();
  saveSelectionFilter = output<SaveDatatableSelectionFilterPayload>();
  editSelectionFilter = output<EditDatatableSelectionFilterPayload>();
  deleteSelectionFilter = output<number>();
  dialogRef = inject(MatDialogRef<DatatableFilterDialogComponent>);
  data: {
    filters: DatatableFilter<any>[];
    activeFilters: { [key: string]: any };
    selectionFilters?: DatatableSelectionFilter[];
    selectedSelectionFilter?: number;
  } = inject(MAT_DIALOG_DATA);

  activeFilters = signal(this.data.activeFilters);
  selectionFilters = signal<DatatableSelectionFilter[]>(this.data.selectionFilters ?? []);
  selectedSelectionFilter = signal<number | undefined>(this.data.selectedSelectionFilter);

  onFilterChange(event: { title: string; value: any }) {
    const current = this.activeFilters();
    this.activeFilters.set({
      ...current,
      [event.title]: event.value,
    });
    this.filterChange.emit(event);
  }

  onSelectionFilterChange(value: number | undefined) {
    this.selectedSelectionFilter.set(value);
    this.applySelectionFilterLocally(value);
    this.selectionFilterChange.emit(value);
  }

  onSaveSelectionFilter(payload: SaveDatatableSelectionFilterPayload) {
    this.saveSelectionFilter.emit(payload);
  }

  onEditSelectionFilter(payload: EditDatatableSelectionFilterPayload) {
    this.selectionFilters.update((filters) =>
      filters.map((filter) =>
        filter.id === payload.id
          ? {
              ...filter,
              label: this.buildFilterLabel(payload.name, filter.count),
              storageName: payload.name,
              trimmedName: this.trimmedName(payload.name),
              filterValues: payload.filterValues,
              showOnDashboard: payload.showOnDashboard,
            }
          : filter,
      ),
    );
    this.selectedSelectionFilter.set(payload.id);
    this.editSelectionFilter.emit(payload);
  }

  onDeleteSelectionFilter(id: number) {
    this.selectionFilters.update((filters) => filters.filter((filter) => filter.id !== id));
    if (this.selectedSelectionFilter() === id) {
      this.selectedSelectionFilter.set(undefined);
    }
    this.deleteSelectionFilter.emit(id);
  }

  private applySelectionFilterLocally(value: number | undefined) {
    const selectedFilter = this.selectionFilters().find((filter) => filter.id === value);
    const filterValues = selectedFilter?.filterValues ?? {};

    const nextFilters = this.data.filters.reduce(
      (acc, filter) => {
        let nextValue = filterValues[filter.title];
        if (
          (filter.type === 'multi-select' || filter.type === 'process-id') &&
          Array.isArray(nextValue) &&
          nextValue.length === 0
        ) {
          nextValue = undefined;
        }
        if (nextValue !== undefined) acc[filter.title] = nextValue;
        return acc;
      },
      {} as { [key: string]: any },
    );

    this.activeFilters.set(nextFilters);
  }

  private trimmedName(name: string) {
    return name.length > 18 ? `${name.substring(0, 18)}...` : name;
  }

  private buildFilterLabel(name: string, count?: number) {
    const trimmedName = this.trimmedName(name);
    return count !== undefined ? `${trimmedName} (${count})` : trimmedName;
  }
}
